import { API_DOMAIN } from 'constants/api';
import { SLICE_NAME } from 'constants/common';
import { BACKGROUND_PATH } from 'constants/config';
import { THEME_KEY } from 'constants/theme';
import store from 'store/store';

function generateContrastColor(baseHex, targetRatio = 4) {
    // for (let v = 0; v <= 255; v++) {
    //   const test1 = rgbToHex(v, v, v); // màu xám sáng/dark
    //   if (contrastRatio(baseHex, test1) >= targetRatio) return test1;
    // }

    return '#FFFFFF';
}

// Chuyển hex → RGB
export function hexToRgbColor(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3)
        hex = hex
            .split('')
            .map((x) => x + x)
            .join('');

    const bigint = parseInt(hex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
}

// RGB → HEX
function rgbToHexColor({ r, g, b }) {
    const toHex = (c) => c.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Adjust opacity
function applyOpacity(hex, opacity) {
    const rgb = hexToRgbColor(hex);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
}

// Lighten color by % (0.1 = 10%)
function lighten(hex, amount) {
    const rgb = hexToRgbColor(hex);
    const lightenChannel = (c) => Math.min(255, Math.floor(c + (255 - c) * amount));

    return rgbToHexColor({
        r: lightenChannel(rgb.r),
        g: lightenChannel(rgb.g),
        b: lightenChannel(rgb.b),
    });
}

// Mix two colors by ratio (weight = phần trăm B)
function mix(colorA, colorB, weight = 0.5) {
    const A = hexToRgbColor(colorA);
    const B = hexToRgbColor(colorB);

    const mixChannel = (a, b) => Math.round(a * (1 - weight) + b * weight);

    return rgbToHexColor({
        r: mixChannel(A.r, B.r),
        g: mixChannel(A.g, B.g),
        b: mixChannel(A.b, B.b),
    });
}

export function hexToHSL(hex) {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((x) => x + x)
            .join('');
    }

    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;

    const d = max - min;

    if (d === 0) {
        h = 0;
        s = 0;
    } else {
        s = d / (1 - Math.abs(2 * l - 1));

        // eslint-disable-next-line default-case
        switch (max) {
            case r:
                h = ((g - b) / d) % 6;
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h = Math.round((h ?? 0) * 60);
        if (h < 0) h += 360;
    }

    return {
        h,
        s: +(s * 100).toFixed(2),
        l: +(l * 100).toFixed(2),
    };
}

export function hslWithChangeToHex(h, s, l, sChange = 1, lChange = 1) {
    // 1) Thay đổi giá trị
    s = Math.min(90, s * sChange);
    l = Math.min(90, l * lChange);

    // Convert về range 0–1
    const _s = s / 100;
    const _l = l / 100;

    const c = (1 - Math.abs(2 * _l - 1)) * _s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = _l - c / 2;

    let r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    // Convert về 0–255
    const R = Math.round((r + m) * 255);
    const G = Math.round((g + m) * 255);
    const B = Math.round((b + m) * 255);

    // Format HEX 2 chữ số
    const toHex = (v) => v.toString(16).padStart(2, '0');

    return '#' + toHex(R) + toHex(G) + toHex(B);
}

export function hslAddLToHex(h, s, l, lChange = 0) {
    // Normalize & clamp inputs
    // wrap hue to [0,360)
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(90, s)); // clamp S to [0,90]
    l = l + lChange;
    l = Math.max(0, Math.min(90, l)); // clamp L to [0,90]

    const _s = s / 100;
    const _l = l / 100;

    const c = (1 - Math.abs(2 * _l - 1)) * _s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = _l - c / 2;

    let r = 0,
        g = 0,
        b = 0;
    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } /*300 <= h < 360*/ else {
        r = c;
        g = 0;
        b = x;
    }

    const R = Math.round((r + m) * 255);
    const G = Math.round((g + m) * 255);
    const B = Math.round((b + m) * 255);

    const toHex = (v) => v.toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(R)}${toHex(G)}${toHex(B)}`;
}

export function getColor({ primary, secondary, text }) {
    const { h: ht, s: st, l: lt } = hexToHSL(text);
    const { h: hp, s: sp, l: lp } = hexToHSL(primary);

    return {
        T0: text,
        T1: hslWithChangeToHex(ht, st, lt, 0.7, 1.4),
        T2: hslWithChangeToHex(ht, st, lt, 0.5, 1.7),
        T3: hslWithChangeToHex(ht, st, lt, 0.5, 2),
        T4: hslWithChangeToHex(ht, st, lt, 0.3, 5),
        P0: primary,
        P1: hslAddLToHex(hp, sp, lp, 10),
        P2: hslAddLToHex(hp, sp, lp, 50),
        P3: hslAddLToHex(hp, sp, lp, 90),
        S0: secondary,
    };
}

function normalizeColors({ primary, secondary, text, isVMPTheme }) {
    if (isVMPTheme)
        return {
            // TEXT
            T0: text,
            T1: '#606060',
            T2: applyOpacity(text, 0.5),
            T3: applyOpacity(text, 0.25),
            T4: '#AAAAAA',

            // PRIMARY
            P0: primary,
            P1: lighten(primary, 0.1),
            P2: lighten(primary, 0.3),
            P3: '#EE00330A',
            PS: '#C4132B',
            PP: '#EE0033',

            // SECONDARY
            S0: secondary,
            S1: '#EF929C',

            // MIX (M1 = 80% C0 + 20% P0) — C0 tạm = secondary
            M1: mix(secondary, primary, 0.2),
        };

    const normalizedText = text?.toLowerCase();
    const isBlack = normalizedText === '#000' || normalizedText === '#000000';
    const color = getColor({ primary, secondary, text})
    const T1 = isBlack ? '#44494d' : color.T1;
    const T2 = isBlack ? '#73777A' : color.T2;
    const T3 = isBlack ? '#8F9294' : color.T3;
    const T4 = isBlack ? '#E9E9E9' : color.T4;

    return {
        // TEXT
        T0: text,
        T1,
        T2,
        T3,
        T4,

        // PRIMARY
        P0: primary,
        P1: color.P1,
        P2: color.P2,
        P3: color.P3,
        PS: primary,
        PP: color.P1,

        // SECONDARY
        S0: secondary,
        S1: color.P1,

        // MIX (M1 = 80% C0 + 20% P0) — C0 tạm = secondary
        M1: mix(secondary, primary, 0.2),
    };
}

function getBackgroundSystemUrl(bgUrl) {
    if (bgUrl) {
        if (bgUrl.includes(BACKGROUND_PATH)) return `${API_DOMAIN}${bgUrl}`;
        return bgUrl;
    }

    return '';
}

function getCurrentColorSystem() {
    const draftConfig = store.getState()[SLICE_NAME.THEME].draftConfig;
    const c = normalizeColors({
        primary: draftConfig[THEME_KEY.PRIMARY],
        secondary: draftConfig[THEME_KEY.SECONDARY],
        text: draftConfig[THEME_KEY.TEXT],
        isVMPTheme: draftConfig[THEME_KEY.IS_DEFAULT],
    });

    return c;
}

function isDefaultColorSystem() {
    const draftConfig = store.getState()[SLICE_NAME.THEME].draftConfig;
    return Boolean(draftConfig[THEME_KEY.IS_DEFAULT]);
}

export { generateContrastColor, normalizeColors, getBackgroundSystemUrl, getCurrentColorSystem, isDefaultColorSystem };
